import { SidebarChat, useSidebarChat } from '@platform/chat';
import {
  type ArtifactConversationResolveResponse,
  useLazyResolveArtifactConversationQuery,
} from '@platform/chat/api/chatRestoreApi';
import { chatMessageEntrypoint } from '@platform/chat/messageEntrypoint';
import { EditableSummary } from '@platform/editor';
import { copyMarkdownToClipboard } from '@platform/markdown';
import {
  clearActiveRun,
  isAwaitingPersistedVersion,
  selectIsHydratingSummaryReplay,
  selectIsSummaryStreaming,
  selectSummaryActiveRun,
  selectSummarySelection,
  shouldShowStreamingSummary,
} from '@platform/summary-ui';
import { type FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query';
import { Alert, Button, Icon, IconButton, Paper, Tooltip, Typography } from '@shared/ui';
import { normalizeNilUuid } from '@shared/utils';
import React from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useBreadcrumbOverrides } from '../../layout/navigation/BreadcrumbOverridesContext';
import { isCoachUser, isMemberSafePath, isMemberUser } from '../../platform/auth/userRoles';
import { analytics } from '../../services/analytics';
import { useGetTeamQuery } from '../../store/api/apiSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getMemberDisplayName } from '../members/types';
import {
  useGetArtifactQuery,
  useListArtifactVersionsQuery,
  useShareArtifactMutation,
  useUpdateArtifactMutation,
} from './api/artifactApi';
import {
  type FeedbackType,
  useCreateArtifactFeedbackMutation,
  useGetLatestArtifactFeedbackQuery,
} from './api/artifactFeedbackApi';
import ArtifactPageWithChatLayout from './components/ArtifactPageWithChatLayout';
import { ArtifactVersionDropdown } from './components/ArtifactVersionDropdown';
import { FeedbackModal } from './components/FeedbackModal';
import { ShareButton } from './components/ShareButton';
import { ShareConfirmationDialog } from './components/ShareConfirmationDialog';
import {
  countTranscriptSessionSources,
  formatArtifactHeaderDateFromTranscriptSessions,
} from './utils/artifactHeaderDate';
import { getArtifactTypeLabel } from './utils/artifactLabels';
import { toSummaryPageContextFromArtifact } from './utils/artifactToPageContext';
import { resolveFeedbackTarget } from './utils/feedbackTarget';
import { isProfileShareable, PROFILE_SLUGS, profileSlugToArtifactType } from './utils/profileSlugs';
import { formatShareSessionDateFromTranscriptSessions } from './utils/shareSessionDate';

const parseErrorMessage = (error: unknown, fallback: string): string => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null) {
    const maybeStatus = error as { data?: unknown; error?: string };
    if (maybeStatus.data && typeof maybeStatus.data === 'object') {
      const data = maybeStatus.data as Record<string, unknown>;
      if (typeof data.error === 'string' && data.error.trim().length > 0) {
        return data.error;
      }
      if (typeof data.message === 'string' && data.message.trim().length > 0) {
        return data.message;
      }
    }
    if (typeof maybeStatus.error === 'string' && maybeStatus.error.trim().length > 0) {
      return maybeStatus.error;
    }
  }
  return fallback;
};

const isNotFound = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const fbq = error as FetchBaseQueryError;
  return 'status' in fbq && fbq.status === 404;
};

const isForbidden = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const fbq = error as FetchBaseQueryError;
  return 'status' in fbq && fbq.status === 403;
};

const CANONICAL_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const MEMBER_REFLECTION_CHAT_STARTER = `This reflection is generated from your session transcript — not a summary of what was said, but a coaching lens on what emerged. It surfaces patterns, shifts, and moments worth carrying forward.

It's a starting point, not the final word. If something doesn't resonate, or you want to go deeper on any part of it, ask below.`;

const parseCanonicalConversationID = (rawValue: string | null): string | null => {
  if (!rawValue) return null;
  const normalized = rawValue.trim();
  if (!CANONICAL_UUID_PATTERN.test(normalized)) {
    return null;
  }
  return normalized;
};

type ArtifactBreadcrumbOverridesProps = {
  artifactLabel: string | null;
  memberId: string | null;
};

const ArtifactBreadcrumbOverrides = ({
  artifactLabel,
  memberId,
}: ArtifactBreadcrumbOverridesProps): React.ReactElement | null => {
  const { setBreadcrumbOverrides } = useBreadcrumbOverrides();

  React.useEffect(() => {
    const normalizedMemberId = memberId?.trim() || null;
    const normalizedArtifactLabel = artifactLabel?.trim() || undefined;

    setBreadcrumbOverrides({
      memberContext: normalizedMemberId ? { memberId: normalizedMemberId } : null,
      labels: { artifactLabel: normalizedArtifactLabel },
    });
  }, [artifactLabel, memberId, setBreadcrumbOverrides]);

  return null;
};

export default function TranscriptArtifactDetailPage(): React.ReactElement {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { artifactId } = useParams();
  const personId = useAppSelector((s) => s.auth.user?.id) || '';
  const authUser = useAppSelector((s) => s.auth.user);

  const safeArtifactId = artifactId ?? '';

  const revisionParam = searchParams.get('revision');
  const parsedRevision = revisionParam ? Number.parseInt(revisionParam, 10) : undefined;
  const invalidRevision = revisionParam !== null && (!parsedRevision || parsedRevision <= 0);
  const hasExplicitRevision = revisionParam !== null && !invalidRevision;
  const resolveLookupKey = React.useMemo(
    () => `${safeArtifactId}:${parsedRevision ?? 'latest'}`,
    [parsedRevision, safeArtifactId]
  );
  const urlConversationID = React.useMemo(
    () => parseCanonicalConversationID(searchParams.get('conversation_id')),
    [searchParams]
  );
  const [resolvedConversation, setResolvedConversation] = React.useState<{
    lookupKey: string;
    conversationID: string | null;
  }>({
    lookupKey: '',
    conversationID: null,
  });
  const resolvedConversationID =
    resolvedConversation.lookupKey === resolveLookupKey
      ? resolvedConversation.conversationID
      : null;
  const [resolveArtifactConversation] = useLazyResolveArtifactConversationQuery();
  const [selectedConversationCandidate, setSelectedConversationCandidate] = React.useState<
    string | null
  >(null);
  const selectedConversationCandidateRef = React.useRef<string | null>(null);
  const setSelectedConversationCandidateSafe = React.useCallback((value: string | null) => {
    selectedConversationCandidateRef.current = value;
    setSelectedConversationCandidate(value);
  }, []);

  const artifactQueryArgs =
    safeArtifactId && !invalidRevision
      ? {
          id: safeArtifactId,
          revision: parsedRevision,
        }
      : skipToken;

  const {
    data: artifact,
    error: artifactError,
    isLoading: isArtifactLoading,
    isFetching: isArtifactFetching,
  } = useGetArtifactQuery(artifactQueryArgs, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  const {
    data: versionsData,
    error: versionsError,
    isLoading: isVersionsLoading,
    refetch: refetchVersions,
  } = useListArtifactVersionsQuery(
    safeArtifactId
      ? {
          id: safeArtifactId,
          limit: 15,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );

  const [updateArtifact, { isLoading: isSaving }] = useUpdateArtifactMutation();

  const [markdown, setMarkdown] = React.useState('');
  const [draftMarkdown, setDraftMarkdown] = React.useState('');
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [editSessionId, setEditSessionId] = React.useState<string | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [copyState, setCopyState] = React.useState<'idle' | 'copied' | 'error'>('idle');
  const [runFeedbackTypeByRunID, setRunFeedbackTypeByRunID] = React.useState<
    Record<string, FeedbackType>
  >({});

  const artifactRevision = artifact?.revision ?? null;
  const artifactMarkdown = artifact?.markdown ?? '';
  const artifactTitle = artifact?.title ?? null;
  const artifactDisplayTitle = artifactTitle?.trim() || 'Artifact';
  const artifactSubjectId = normalizeNilUuid(
    (artifact as { subject_id?: string } | undefined)?.subject_id
  );
  const profileSlug = artifact?.profile_slug ?? '';
  const canShareArtifact = isCoachUser(authUser) && isProfileShareable(profileSlug);
  const typeLabel = profileSlug ? getArtifactTypeLabel(profileSlug) : 'Unknown artifact';
  const latestRevision = versionsData?.versions?.[0]?.revision ?? artifactRevision ?? 1;
  const currentRevision = artifactRevision ?? latestRevision;
  const isMemberSafeArtifactView = React.useMemo(
    () => isMemberUser(authUser) && isMemberSafePath(location.pathname),
    [authUser, location.pathname]
  );
  const restoreProjection = React.useMemo(() => {
    if (
      !isMemberSafeArtifactView ||
      (profileSlug !== PROFILE_SLUGS.MEMBER_REFLECTION &&
        profileSlug !== PROFILE_SLUGS.CHEM_CALL_REFLECTION_MEMBER)
    ) {
      return undefined;
    }

    return {
      suppressLeadingEntrypoints: [chatMessageEntrypoint.systemAutomation],
      introText: MEMBER_REFLECTION_CHAT_STARTER,
    };
  }, [isMemberSafeArtifactView, profileSlug]);
  const chatContext = React.useMemo(
    () => (artifact ? toSummaryPageContextFromArtifact(artifact) : undefined),
    [artifact]
  );
  const hasChatProfile = profileSlug.trim() !== '';

  React.useEffect(() => {
    if (!safeArtifactId) {
      setResolvedConversation({ lookupKey: '', conversationID: null });
      return;
    }

    setResolvedConversation({ lookupKey: resolveLookupKey, conversationID: null });

    let cancelled = false;
    void resolveArtifactConversation({
      artifactId: safeArtifactId,
      ...(typeof parsedRevision === 'number' && parsedRevision > 0
        ? { revision: parsedRevision }
        : {}),
    })
      .unwrap()
      .then((response: ArtifactConversationResolveResponse) => {
        if (cancelled) return;
        if (response.status === 'found' && response.conversation_id) {
          setResolvedConversation({
            lookupKey: resolveLookupKey,
            conversationID: parseCanonicalConversationID(response.conversation_id),
          });
          return;
        }
        setResolvedConversation({ lookupKey: resolveLookupKey, conversationID: null });
      })
      .catch(() => {
        if (cancelled) return;
        setResolvedConversation({ lookupKey: resolveLookupKey, conversationID: null });
      });

    return () => {
      cancelled = true;
    };
  }, [parsedRevision, resolveArtifactConversation, resolveLookupKey, safeArtifactId]);

  const preferredConversationID = React.useMemo(() => {
    if (hasExplicitRevision) {
      return resolvedConversationID ?? urlConversationID;
    }
    return urlConversationID ?? resolvedConversationID;
  }, [hasExplicitRevision, resolvedConversationID, urlConversationID]);

  const secondaryConversationID = React.useMemo(() => {
    if (hasExplicitRevision) {
      return urlConversationID;
    }
    return resolvedConversationID;
  }, [hasExplicitRevision, resolvedConversationID, urlConversationID]);

  React.useEffect(() => {
    setSelectedConversationCandidateSafe(preferredConversationID ?? null);
  }, [preferredConversationID, setSelectedConversationCandidateSafe]);

  const restoreCandidateRef = React.useRef<{
    primary: string | null;
    secondary: string | null;
  }>({
    primary: null,
    secondary: null,
  });
  const pendingRestoreFallbackPrimaryRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    restoreCandidateRef.current = {
      primary: preferredConversationID ?? null,
      secondary: secondaryConversationID ?? null,
    };
  }, [preferredConversationID, secondaryConversationID]);

  const maybeFallbackToSecondaryConversation = React.useCallback(
    (error: unknown) => {
      const candidates = restoreCandidateRef.current;
      const primary = candidates.primary;
      const secondary = candidates.secondary;
      if (!primary || primary === secondary) {
        return;
      }

      const current = selectedConversationCandidateRef.current;
      if (current && current !== primary) {
        return;
      }

      const extractStatus = (candidate: unknown): number | null => {
        if (typeof candidate !== 'object' || candidate === null) {
          return null;
        }
        const directStatus = (candidate as { status?: unknown }).status;
        if (typeof directStatus === 'number') {
          return directStatus;
        }
        const nestedStatus = (candidate as { error?: { status?: unknown } }).error?.status;
        if (typeof nestedStatus === 'number') {
          return nestedStatus;
        }
        return null;
      };

      const status = extractStatus(error);
      if (status !== 403 && status !== 404) {
        return;
      }

      if (!secondary) {
        pendingRestoreFallbackPrimaryRef.current = primary;
        return;
      }

      pendingRestoreFallbackPrimaryRef.current = null;
      setSelectedConversationCandidateSafe(secondary);
    },
    [setSelectedConversationCandidateSafe]
  );

  React.useEffect(() => {
    const pendingPrimary = pendingRestoreFallbackPrimaryRef.current;
    if (!pendingPrimary) {
      return;
    }

    if (!secondaryConversationID) {
      return;
    }

    if (selectedConversationCandidate && selectedConversationCandidate !== pendingPrimary) {
      pendingRestoreFallbackPrimaryRef.current = null;
      return;
    }

    pendingRestoreFallbackPrimaryRef.current = null;
    setSelectedConversationCandidateSafe(secondaryConversationID);
  }, [
    secondaryConversationID,
    selectedConversationCandidate,
    setSelectedConversationCandidateSafe,
  ]);

  const chat = useSidebarChat({
    convId: selectedConversationCandidate ?? undefined,
    profile: profileSlug,
    autoConnect: hasChatProfile,
    displayMode: 'ui',
    context: chatContext,
    onRestoreError: maybeFallbackToSecondaryConversation,
    restoreProjection,
  });
  const selection = useAppSelector(selectSummarySelection(chat.convId));
  const activeRun = useAppSelector(selectSummaryActiveRun(chat.convId));
  const isSummaryStreaming = useAppSelector(selectIsSummaryStreaming(chat.convId));
  const isHydratingSummaryReplay = useAppSelector(selectIsHydratingSummaryReplay(chat.convId));
  const lastSyncedSelectionRef = React.useRef<string | null>(null);

  // Map profile_slug to artifact_type for analytics
  const artifactType = React.useMemo(() => profileSlugToArtifactType(profileSlug), [profileSlug]);
  const feedbackTarget = React.useMemo(
    () =>
      resolveFeedbackTarget({
        selectedArtifactId: safeArtifactId,
        activeRun,
      }),
    [activeRun, safeArtifactId]
  );

  // Fetch latest feedback only when artifact-target feedback is active.
  const { data: latestFeedback } = useGetLatestArtifactFeedbackQuery(
    { artifact_id: feedbackTarget?.kind === 'artifact' ? feedbackTarget.artifactId : '' },
    {
      skip: feedbackTarget?.kind !== 'artifact',
      refetchOnMountOrArgChange: true,
    }
  );
  const latestFeedbackType =
    feedbackTarget?.kind === 'artifact'
      ? latestFeedback?.feedback?.feedback_type || null
      : feedbackTarget?.kind === 'run'
        ? runFeedbackTypeByRunID[feedbackTarget.runId] || null
        : null;

  const [createFeedback, { isLoading: isSubmittingFeedback }] = useCreateArtifactFeedbackMutation();

  const [feedbackModalOpen, setFeedbackModalOpen] = React.useState(false);
  const [feedbackType, setFeedbackType] = React.useState<FeedbackType | null>(null);
  const [feedbackError, setFeedbackError] = React.useState<string | null>(null);

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = React.useState(false);
  const [shareError, setShareError] = React.useState<string | null>(null);

  // Share mutation
  const [shareArtifact, { isLoading: isSharing }] = useShareArtifactMutation();

  // Get team data to find member email
  const { data: teamData } = useGetTeamQuery(undefined, { skip: isMemberUser(authUser) });
  const shareTargetMemberId = artifactSubjectId || '';
  const member = teamData?.members?.find((m) => m.person.id === shareTargetMemberId);
  const memberEmail = member?.person?.primary_email ?? '';
  const memberName = member ? getMemberDisplayName(member) : 'Member';
  const coachName = React.useMemo(() => {
    return artifact?.owner_display_name?.trim() || null;
  }, [artifact?.owner_display_name]);

  // Check if current version has been shared (compare shared_revision with current revision)
  const isCurrentVersionShared =
    artifact?.shared_revision != null && artifact.shared_revision === artifact.revision;

  React.useEffect(() => {
    if (artifactRevision === null) {
      return;
    }

    setMarkdown(artifactMarkdown);
  }, [artifactRevision, artifactMarkdown]);

  React.useEffect(() => {
    if (isEditing) {
      return;
    }
    setDraftMarkdown(markdown);
  }, [isEditing, markdown]);

  React.useEffect(() => {
    if (!chatContext) return;
    chat.updateContext({
      documentIds: chatContext.documentIds,
      transcriptMetadata: chatContext.transcriptMetadata,
      currentArtifact: chatContext.currentArtifact,
    });
  }, [chat.updateContext, chatContext]);

  React.useEffect(() => {
    if (chat.isHydratingReplay || isHydratingSummaryReplay) {
      return;
    }
    if (!selection || !safeArtifactId || selection.artifactId !== safeArtifactId) {
      return;
    }

    const selectionKey = `${selection.artifactId}:${selection.revision}:${selection.requestedAt}`;
    if (lastSyncedSelectionRef.current === selectionKey) {
      return;
    }
    lastSyncedSelectionRef.current = selectionKey;
    void refetchVersions();

    if (selection.revision <= 0 || parsedRevision === selection.revision) {
      return;
    }

    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set('revision', selection.revision.toString());
    setSearchParams(nextSearchParams, { replace: true });
  }, [
    chat.isHydratingReplay,
    isHydratingSummaryReplay,
    location.search,
    parsedRevision,
    refetchVersions,
    safeArtifactId,
    selection,
    setSearchParams,
  ]);

  React.useEffect(() => {
    if (chat.isHydratingReplay || isHydratingSummaryReplay) {
      return;
    }
    const canonicalConversationID = parseCanonicalConversationID(chat.convId);
    if (!canonicalConversationID) {
      return;
    }
    if (searchParams.get('conversation_id') === canonicalConversationID) {
      return;
    }

    setSearchParams(
      (prevSearchParams) => {
        if (prevSearchParams.get('conversation_id') === canonicalConversationID) {
          return prevSearchParams;
        }
        const nextSearchParams = new URLSearchParams(prevSearchParams);
        nextSearchParams.set('conversation_id', canonicalConversationID);
        return nextSearchParams;
      },
      { replace: true }
    );
  }, [
    chat.convId,
    chat.isHydratingReplay,
    isHydratingSummaryReplay,
    searchParams,
    setSearchParams,
  ]);

  React.useEffect(() => {
    if (!chat.convId) return;
    if (!activeRun) return;
    if (!selection) return;
    if (activeRun.status === 'started' || activeRun.status === 'streaming') return;
    if (!activeRun.artifactId || !activeRun.revision) return;
    if (activeRun.artifactId !== selection.artifactId) return;
    if (activeRun.revision !== selection.revision) return;
    if (!artifact) return;
    if (artifact.revision !== activeRun.revision) return;
    if (activeRun.streamingMarkdown.trim() === '') return;

    dispatch(clearActiveRun({ convId: chat.convId, stableId: activeRun.stableId }));
  }, [activeRun, artifact, chat.convId, dispatch, selection]);

  React.useEffect(() => {
    if (!artifact || !safeArtifactId || invalidRevision) return;
    analytics.viewed(
      'Viewed artifact',
      'summary',
      location.pathname,
      {
        artifact_id: safeArtifactId,
        member_id: artifactSubjectId,
        profile_slug: artifact.profile_slug,
        artifact_type_label: typeLabel,
        revision: artifact.revision,
      },
      {
        dedupeKey: `viewed-artifact:${location.key}:${safeArtifactId}:${artifact.revision}`,
      }
    );
  }, [
    artifact,
    invalidRevision,
    location.key,
    location.pathname,
    safeArtifactId,
    artifactSubjectId,
    typeLabel,
  ]);

  const handleEditStart = React.useCallback(() => {
    const newEditSessionId = crypto.randomUUID();
    setEditSessionId(newEditSessionId);
    setDraftMarkdown(markdown);
    setIsEditing(true);
    const props: Record<string, unknown> = {
      profile_slug: profileSlug,
      artifact_type_label: typeLabel,
      edit_session_id: newEditSessionId,
      from_screen: 'summary',
      revision: currentRevision,
    };
    if (safeArtifactId) {
      props.artifact_id = safeArtifactId;
    }
    analytics.track('Edited artifact', props);
  }, [markdown, currentRevision, profileSlug, safeArtifactId, typeLabel]);

  const handleCancelEdit = React.useCallback(() => {
    setDraftMarkdown(markdown);
    setIsEditing(false);
    const props: Record<string, unknown> = {
      profile_slug: profileSlug,
      artifact_type_label: typeLabel,
    };
    if (safeArtifactId) {
      props.artifact_id = safeArtifactId;
    }
    if (editSessionId) {
      props.edit_session_id = editSessionId;
    }
    analytics.track('Canceled edit artifact', props);
  }, [markdown, profileSlug, safeArtifactId, editSessionId, typeLabel]);

  React.useEffect(() => {
    if (copyState === 'idle') return;
    const timeout = window.setTimeout(() => setCopyState('idle'), 2000);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  const handleSave = React.useCallback(
    async (editedMarkdown: string) => {
      if (!personId || !safeArtifactId) {
        const message = 'Cannot save: missing artifact ID or person ID.';
        setSaveError(message);
        throw new Error(message);
      }
      if (!artifactRevision) {
        const message = 'Cannot save: missing artifact revision. Please reload and try again.';
        setSaveError(message);
        throw new Error(message);
      }

      setSaveError(null);
      try {
        const response = await updateArtifact({
          id: safeArtifactId,
          markdown: editedMarkdown,
          revision: artifactRevision,
          actor_id: personId,
        }).unwrap();

        setMarkdown(editedMarkdown);
        setIsEditing(false);
        if (searchParams.has('revision')) {
          const nextSearchParams = new URLSearchParams(searchParams);
          nextSearchParams.delete('revision');
          setSearchParams(nextSearchParams);
        }

        const props: Record<string, unknown> = {
          profile_slug: profileSlug,
          artifact_type_label: typeLabel,
          revision: response.revision,
        };
        if (safeArtifactId) {
          props.artifact_id = safeArtifactId;
        }
        if (editSessionId) {
          props.edit_session_id = editSessionId;
        }
        analytics.track('Saved artifact', props);
      } catch (err) {
        const message = parseErrorMessage(err, 'Save failed. Please try again.');
        setSaveError(message);
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [
      artifactRevision,
      editSessionId,
      personId,
      profileSlug,
      safeArtifactId,
      searchParams,
      setSearchParams,
      typeLabel,
      updateArtifact,
    ]
  );

  const handleSummaryChange = React.useCallback(
    (value: string) => {
      if (isEditing) {
        setDraftMarkdown(value);
      } else {
        setMarkdown(value);
      }
      setSaveError(null);
    },
    [isEditing]
  );

  const handleSendMessage = React.useCallback(
    (text: string) => chat.sendMessage(text),
    [chat.sendMessage]
  );

  const handleVersionSelect = React.useCallback(
    (revision: number) => {
      const props: Record<string, unknown> = {
        profile_slug: profileSlug,
        artifact_type_label: typeLabel,
        revision,
      };
      if (safeArtifactId) {
        props.artifact_id = safeArtifactId;
      }
      analytics.track('Selected artifact version', props);

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('conversation_id');
      if (revision === latestRevision) {
        nextSearchParams.delete('revision');
        setSearchParams(nextSearchParams);
        return;
      }
      nextSearchParams.set('revision', revision.toString());
      setSearchParams(nextSearchParams);
    },
    [latestRevision, profileSlug, safeArtifactId, searchParams, setSearchParams, typeLabel]
  );

  const handleOpenFeedback = React.useCallback(
    (type: FeedbackType) => {
      if (!feedbackTarget) return;
      setFeedbackType(type);
      setFeedbackError(null);
      setFeedbackModalOpen(true);
    },
    [feedbackTarget]
  );

  const handleCloseFeedback = React.useCallback(() => {
    setFeedbackModalOpen(false);
    setFeedbackError(null);
  }, []);

  const handleSubmitFeedback = React.useCallback(
    async (feedbackText: string) => {
      if (!feedbackTarget || !feedbackType) return;

      setFeedbackError(null);
      try {
        const feedbackContext: Record<string, unknown> = {
          version: 1,
        };
        if (chat.convId) {
          feedbackContext.conversation_id = chat.convId;
        }
        if (feedbackTarget.kind === 'artifact') {
          feedbackContext.artifact_id = feedbackTarget.artifactId;
        } else {
          feedbackContext.run_id = feedbackTarget.runId;
        }
        await createFeedback({
          feedback_type: feedbackType,
          feedback_text: feedbackText || undefined,
          context: feedbackContext,
        }).unwrap();
        if (feedbackTarget.kind === 'run') {
          setRunFeedbackTypeByRunID((prev) => ({
            ...prev,
            [feedbackTarget.runId]: feedbackType,
          }));
        }

        // Track analytics event
        analytics.track('User Submitted Feedback', {
          profile_slug: profileSlug,
          artifact_type_label: typeLabel,
          feedback_type: feedbackType,
          feedback_text: feedbackText || '',
          feedback_target_type: feedbackTarget.kind,
          artifact_id: feedbackTarget.kind === 'artifact' ? feedbackTarget.artifactId : undefined,
          run_id: feedbackTarget.kind === 'run' ? feedbackTarget.runId : undefined,
          artifact_type: artifactType || profileSlug || 'unknown',
          has_text: feedbackText.trim().length > 0,
          text_length: feedbackText.length,
        });

        // Close modal on success
        setFeedbackModalOpen(false);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to submit feedback. Please try again.';
        setFeedbackError(message);
        // Keep modal open on error
      }
    },
    [
      feedbackTarget,
      artifactType,
      profileSlug,
      feedbackType,
      createFeedback,
      typeLabel,
      chat.convId,
    ]
  );

  // Share handlers
  const handleOpenShareDialog = React.useCallback(() => {
    setShareError(null);
    setShareDialogOpen(true);
  }, []);

  const handleCloseShareDialog = React.useCallback(() => {
    setShareDialogOpen(false);
    setShareError(null);
  }, []);

  const handleConfirmShare = React.useCallback(async () => {
    if (!canShareArtifact) {
      setShareError('This artifact type cannot be shared');
      return;
    }
    if (!artifact || !memberEmail) {
      setShareError('Unable to share: missing artifact or member information');
      return;
    }

    try {
      await shareArtifact({
        id: artifact.id,
        recipient_email: memberEmail,
      }).unwrap();

      setShareDialogOpen(false);
      // TODO: Show success toast
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to share artifact';
      setShareError(message);
    }
  }, [artifact, canShareArtifact, memberEmail, shareArtifact]);

  const shareSessionDate = React.useMemo(
    () => formatShareSessionDateFromTranscriptSessions(artifact?.transcript_sessions),
    [artifact?.transcript_sessions]
  );
  const expectedArtifactHeaderSessionCount = React.useMemo(
    () => countTranscriptSessionSources(artifact?.sources),
    [artifact?.sources]
  );
  const artifactHeaderDate = React.useMemo(
    () =>
      formatArtifactHeaderDateFromTranscriptSessions(
        artifact?.transcript_sessions,
        expectedArtifactHeaderSessionCount
      ),
    [artifact?.transcript_sessions, expectedArtifactHeaderSessionCount]
  );
  const artifactHeaderMetadata = React.useMemo(() => {
    const participantNames = [member ? memberName : null, coachName].filter(Boolean).join(' & ');
    return [participantNames || null, artifactHeaderDate].filter(Boolean).join(' · ');
  }, [artifactHeaderDate, coachName, member, memberName]);

  const loadErrorMessage = invalidRevision
    ? 'Invalid revision parameter.'
    : artifactError
      ? isForbidden(artifactError)
        ? "You don't have access to this artifact."
        : parseErrorMessage(artifactError, 'Failed to load artifact.')
      : null;

  // When the artifact itself failed, a version-list failure adds nothing.
  const versionsErrorMessage =
    versionsError && !artifactError
      ? parseErrorMessage(versionsError, 'Failed to load versions.')
      : null;

  const showNotFound = artifactError && isNotFound(artifactError);

  const isLoading = (isArtifactLoading || isArtifactFetching) && !artifactError && !invalidRevision;
  const disableDropdown =
    isVersionsLoading || !!versionsErrorMessage || isSummaryStreaming || chat.isStreaming;
  const showSummary = Boolean(artifact) && !loadErrorMessage;
  const streamingMarkdown = activeRun?.streamingMarkdown ?? '';
  const pendingVersionGeneration = isAwaitingPersistedVersion(activeRun);
  const shouldShowStreaming = shouldShowStreamingSummary({
    streamingMarkdown,
    isSummaryStreaming,
    activeRun,
    selection,
    artifactDataRevision: artifact?.revision ?? null,
  });
  const displayMarkdown = shouldShowStreaming ? streamingMarkdown : markdown;

  const handleCopySummary = React.useCallback(async () => {
    if (!displayMarkdown) return;
    try {
      await copyMarkdownToClipboard(displayMarkdown);
      setCopyState('copied');
      const props: Record<string, unknown> = {
        profile_slug: profileSlug,
        artifact_type_label: typeLabel,
      };
      if (safeArtifactId) {
        props.artifact_id = safeArtifactId;
      }
      analytics.track('Copied artifact', props);
    } catch {
      setCopyState('error');
    }
  }, [displayMarkdown, profileSlug, safeArtifactId, typeLabel]);

  const copyTooltipTitle =
    copyState === 'copied' ? 'Copied!' : copyState === 'error' ? 'Copy failed' : 'Copy summary';
  const rightSidebar =
    hasChatProfile && chatContext ? (
      <SidebarChat
        storageId={`artifact-view-${safeArtifactId || artifact?.id || 'unknown'}`}
        entities={chat.entities}
        convId={chat.convId}
        onSendMessage={handleSendMessage}
        isLoading={chat.isStreaming}
        placeholder="Ask questions or request changes..."
        showHeader={false}
      />
    ) : null;

  return (
    <ArtifactPageWithChatLayout rightSidebar={rightSidebar}>
      <ArtifactBreadcrumbOverrides artifactLabel={artifactTitle} memberId={artifactSubjectId} />
      <div className="flex flex-col gap-3 h-full min-h-0">
        {loadErrorMessage ? (
          <Alert severity={showNotFound ? 'warning' : 'error'}>{loadErrorMessage}</Alert>
        ) : null}
        {versionsErrorMessage ? <Alert severity="error">{versionsErrorMessage}</Alert> : null}

        <Paper className="p-4 flex-1 min-h-0 overflow-auto flex flex-col">
          {isLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          ) : showSummary && artifact ? (
            <div className="h-full min-h-0 flex flex-col">
              {/* Header line: version dropdown (left) + copy/edit icons (right) */}
              <div className="flex items-center justify-between gap-3 mb-4">
                <ArtifactVersionDropdown
                  typeLabel={artifactDisplayTitle}
                  currentRevision={currentRevision}
                  versions={versionsData?.versions ?? []}
                  onVersionSelect={handleVersionSelect}
                  disabled={disableDropdown}
                  pendingGeneration={pendingVersionGeneration}
                />
                <div className="flex items-center gap-1">
                  {isEditing ? (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                          try {
                            await handleSave(draftMarkdown);
                          } catch {
                            // Error handled by handleSave
                          }
                        }}
                        disabled={isSaving || draftMarkdown === markdown}
                        size="small"
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </Button>
                      <IconButton
                        aria-label="Cancel edit"
                        size="small"
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                      >
                        <Icon fontSize="small">close</Icon>
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Tooltip title={copyTooltipTitle}>
                        <span>
                          <IconButton
                            aria-label="Copy summary to clipboard"
                            size="small"
                            onClick={handleCopySummary}
                            disabled={!displayMarkdown}
                          >
                            <Icon fontSize="small">content_copy</Icon>
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={feedbackTarget ? 'What works well' : 'Generating summary...'}>
                        <span>
                          <IconButton
                            aria-label="Thumbs up"
                            size="small"
                            onClick={() => handleOpenFeedback('positive')}
                            disabled={!feedbackTarget}
                            sx={{
                              color: latestFeedbackType === 'positive' ? 'primary.main' : 'inherit',
                            }}
                          >
                            <Icon fontSize="small">
                              {latestFeedbackType === 'positive' ? 'thumb_up' : 'thumb_up_off_alt'}
                            </Icon>
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={feedbackTarget ? 'What could be improved' : 'Generating summary...'}
                      >
                        <span>
                          <IconButton
                            aria-label="Thumbs down"
                            size="small"
                            onClick={() => handleOpenFeedback('negative')}
                            disabled={!feedbackTarget}
                            sx={{
                              color: latestFeedbackType === 'negative' ? 'primary.main' : 'inherit',
                            }}
                          >
                            <Icon fontSize="small">
                              {latestFeedbackType === 'negative'
                                ? 'thumb_down'
                                : 'thumb_down_off_alt'}
                            </Icon>
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit summary">
                        <span>
                          <IconButton
                            aria-label="Edit summary"
                            size="small"
                            onClick={handleEditStart}
                            disabled={isSummaryStreaming || chat.isStreaming}
                          >
                            <Icon fontSize="small">edit</Icon>
                          </IconButton>
                        </span>
                      </Tooltip>
                      {canShareArtifact && (
                        <ShareButton
                          isShared={isCurrentVersionShared}
                          onClick={handleOpenShareDialog}
                          disabled={!artifact || !memberEmail}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
              {artifactHeaderMetadata ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {artifactHeaderMetadata}
                </Typography>
              ) : null}
              {saveError && (
                <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                  {saveError}
                </Typography>
              )}
              <div className="flex-1 min-h-0 overflow-auto mp-block artifact-content">
                <EditableSummary
                  value={isEditing ? draftMarkdown : displayMarkdown}
                  onChange={handleSummaryChange}
                  onEditStart={handleEditStart}
                  onCancelEdit={handleCancelEdit}
                  onCopy={handleCopySummary}
                  onSave={handleSave}
                  readOnly={isSummaryStreaming || chat.isStreaming}
                  isSaving={isSaving}
                  saveError={saveError}
                  showEditButton={false}
                  isEditing={isEditing}
                  onEditingChange={setIsEditing}
                  feedback={
                    safeArtifactId
                      ? {
                          artifactId: safeArtifactId,
                          artifactType: artifactType || profileSlug || 'unknown',
                          latestFeedbackType,
                          onOpen: handleOpenFeedback,
                        }
                      : undefined
                  }
                />
              </div>
            </div>
          ) : null}
        </Paper>
        <FeedbackModal
          open={feedbackModalOpen}
          feedbackType={feedbackType}
          onClose={handleCloseFeedback}
          onSubmit={handleSubmitFeedback}
          isSubmitting={isSubmittingFeedback}
          error={feedbackError}
        />
        <ShareConfirmationDialog
          open={shareDialogOpen}
          memberName={memberName}
          sessionDate={shareSessionDate}
          version={artifact?.revision ?? 1}
          onClose={handleCloseShareDialog}
          onConfirm={handleConfirmShare}
          isSharing={isSharing}
          error={shareError}
        />
      </div>
    </ArtifactPageWithChatLayout>
  );
}
